"use client";

import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageCircle,
  Sparkles,
  Star,
  Users,
  X,
  Send,
  Upload,
  User,
  Briefcase,
  CheckCircle2,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  fetchAboutContacts,
  fetchClientStats,
  createServiceReviewAction,
} from "@/store/slices/landingSlice";
import Link from "next/link";
import ServiceReviewsSection from "@/views/ServiceReviewsSection";
import { imageUrl } from "../services/BaseUrl";
import { validateImageSize } from "@/utils/imageUtils";

const TestimonialsCTASection = () => {
  const { mode } = useAppSelector((state) => state.theme);
  const isDark = mode === "dark";
  const dispatch = useAppDispatch();
  const { data: aboutData } = useAppSelector(
    (state) => state.landing.aboutContact,
  );
  const { data: statsData } = useAppSelector(
    (state) => state.landing.clientStats,
  );
  const firstAbout = aboutData[0];

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    role: "",
    text: "",
    rating: 5,
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [hoverRating, setHoverRating] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    dispatch(fetchAboutContacts());
    dispatch(fetchClientStats());
  }, [dispatch]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validation = validateImageSize(file);
      if (!validation.valid) {
        setSubmitError(validation.error || "Image size must be under 200KB");
        e.target.value = "";
        return;
      }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.text.trim()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const fd = new FormData();
      fd.append("name", formData.name.trim());
      fd.append("role", formData.role.trim() || "Client");
      fd.append("text", formData.text.trim());
      fd.append("rating", String(formData.rating));
      if (selectedImage) {
        fd.append("image", selectedImage);
      }

      await dispatch(createServiceReviewAction(fd)).unwrap();
      setSubmitSuccess(true);
      setTimeout(() => {
        setIsModalOpen(false);
        setSubmitSuccess(false);
        setFormData({ name: "", role: "", text: "", rating: 5 });
        setSelectedImage(null);
        setImagePreview(null);
      }, 2500);
    } catch (err: any) {
      setSubmitError(
        typeof err === "string"
          ? err
          : "Failed to submit review. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setIsModalOpen(false);
    setSubmitSuccess(false);
    setSubmitError(null);
    setFormData({ name: "", role: "", text: "", rating: 5 });
    setSelectedImage(null);
    setImagePreview(null);
  };

  return (
    <section
      className={`relative py-16 sm:py-20 overflow-hidden transition-colors duration-700 ${
        isDark ? "bg-[#0B0F19]" : "bg-slate-50"
      }`}
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {isDark ? (
          <>
            <motion.div
              className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#8B31FF]/10 rounded-full filter blur-[120px]"
              animate={{
                scale: [1, 1.2, 1],
                x: [0, 50, 0],
                y: [0, 30, 0],
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            <motion.div
              className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-[#8B31FF]/10 rounded-full filter blur-[120px]"
              animate={{
                scale: [1, 1.3, 1],
                x: [0, -50, 0],
                y: [0, -30, 0],
              }}
              transition={{
                duration: 12,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </>
        ) : (
          <>
            <motion.div
              className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#8B31FF]/5 rounded-full filter blur-[100px]"
              animate={{
                scale: [1, 1.2, 1],
                x: [0, 30, 0],
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            <motion.div
              className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-[#8B31FF]/5 rounded-full filter blur-[100px]"
              animate={{
                scale: [1, 1.3, 1],
                x: [0, -30, 0],
              }}
              transition={{
                duration: 12,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </>
        )}

        {/* Grid Pattern */}
        <div
          className={`absolute inset-0 transition-opacity duration-700 ${
            isDark ? "opacity-[0.05]" : "opacity-[0.03]"
          }`}
          style={{
            backgroundImage: isDark
              ? `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`
              : `linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Main CTA */}
        <div className="text-center mb-12 sm:mb-14">
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className={`inline-flex items-center justify-center w-14 h-14 rounded-lg mb-6 shadow-xl backdrop-blur-md border transition-all duration-500 ${
              isDark
                ? "bg-white/5 border-white/10 text-[#8B31FF]"
                : "bg-white/90 border-purple-100 text-[#8B31FF]"
            }`}
          >
            <Sparkles className="w-7 h-7" />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4 sm:mb-6 tracking-tight leading-[1.12] ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            {firstAbout?.mainTitle || "Ready to Become Our Next "} <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B31FF] via-purple-500 to-[#6d1ec9]">
              Success Story?
            </span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className={`md:text-2xl max-w-3xl mx-auto mb-8 transition-colors duration-500 ${
              isDark ? "text-gray-400" : "text-gray-600"
            }`}
          >
            {firstAbout?.subtitle ||
              "Join hundreds of satisfied clients who have transformed their businesses with our innovative digital engineering."}
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-6 justify-center items-center"
          >
            <Link href="/contact">
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
                className={`group px-12 py-4 cursor-pointer font-semibold rounded-lg shadow-2xl flex items-center gap-3 transition-all duration-300 ${
                  isDark
                    ? "bg-white text-[#8B31FF] hover:bg-[#8B31FF] hover:text-black shadow-white/5"
                    : "bg-[#8B31FF] text-white hover:bg-[#6d1ec9] shadow-purple-500/20"
                }`}
              >
                {firstAbout?.ctaButtons[0]?.label || "Start Your Project"}
                <motion.div
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  →
                </motion.div>
              </motion.button>
            </Link>

            <Link href="/careers">
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
                className={`px-12 py-4 backdrop-blur-md font-semibold cursor-pointer border-2 rounded-lg transition-all flex items-center gap-3 ${
                  isDark
                    ? "bg-white/5 border-white/10 text-white hover:bg-white/10"
                    : "bg-white border-[#8B31FF]/20 text-[#8B31FF] hover:bg-gray-50 shadow-sm"
                }`}
              >
                <MessageCircle className="w-6 h-6" />
                {firstAbout?.ctaButtons[1]?.label || "Schedule Experts"}
              </motion.button>
            </Link>
          </motion.div>
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 mb-16 sm:mb-24">
          {[
            {
              icon: Star,
              title: "5-Star Experience",
              description: "Consistently rated 4.9/5 by our partners",
              color: "from-[#8B31FF] to-[#6d1ec9]",
            },
            {
              icon: Users,
              title: "10+ Active Clients",
              description: "Trusted by visionaries worldwide",
              color: "from-[#8B31FF] to-[#6d1ec9]",
            },
            {
              icon: MessageCircle,
              title: "24/7 Strategic Support",
              description: "Dedicated mastery for your mission",
              color: "from-[#8B31FF] to-[#6d1ec9]",
            },
          ].map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                whileHover={{ y: -10 }}
                className={`backdrop-blur-xl border rounded-[1.5rem] p-10 text-center transition-all duration-500 ${
                  isDark
                    ? "bg-white/5 border-white/10"
                    : "bg-white border-purple-50 shadow-[0_10px_40px_rgba(79,17,194,0.02)]"
                }`}
              >
                <div
                  className={`inline-flex p-5 rounded-lg mb-8 bg-gradient-to-br ${feature.color} shadow-lg`}
                >
                  <Icon className="w-8 h-8 text-white" />
                </div>
                <h3
                  className={`text-2xl font-bold mb-4 transition-colors duration-500 ${
                    isDark ? "text-white" : "text-gray-900"
                  }`}
                >
                  {feature.title}
                </h3>
                <p className="font-bold text-[#8B31FF]">
                  {feature.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Client Feedback on Our Expert Services */}
        <ServiceReviewsSection embedded />

        {/* Review Form CTA — "Are You Our Next Partner?" */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
          className={`max-w-4xl mx-auto backdrop-blur-2xl border rounded-[1.5rem] p-12 text-center relative overflow-hidden transition-all duration-500 ${
            isDark
              ? "bg-white/5 border-white/10"
              : "bg-white border-purple-50 shadow-2xl"
          }`}
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#8B31FF] to-[#6d1ec9] opacity-5 -mr-32 -mt-32 blur-[80px] pointer-events-none" />

          <h3
            className={`text-3xl font-black mb-4 transition-colors duration-500 ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            Are You Our Next Partner?
          </h3>
          <p
            className={`text-lg mb-10 transition-colors duration-500 ${
              isDark ? "text-gray-400" : "text-gray-600"
            }`}
          >
            We value the collaborative journey. Share your feedback and join the
            collective excellence of our digital network.
          </p>
          <div>
            <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setIsModalOpen(true)}
              className={`px-10 py-3 rounded-lg cursor-pointer font-semibold shadow-xl transition-all duration-300 inline-flex items-center gap-2 ${
                isDark
                  ? "bg-white text-[#8B31FF] hover:bg-gray-100 shadow-white/5"
                  : "bg-[#8B31FF] text-white hover:bg-[#6d1ec9] shadow-purple-500/20"
              }`}
            >
              <MessageCircle className="w-5 h-5" />
              Share Your Review
            </motion.button>
          </div>
        </motion.div>

        {/* Trust Indicators */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 1 }}
          className="mt-16 sm:mt-24 flex flex-wrap justify-center items-center gap-8 sm:gap-12 md:gap-20"
        >
          {[
            { label: "Partner Satisfaction", value: "98%" },
            { label: "Strategic Success", value: "100%" },
            { label: "Engineering Quality", value: "99%" },
            { label: "Client Retention", value: "98%" },
          ].map((stat, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.1, y: -5 }}
              className="text-center group"
            >
              <div
                className={`text-4xl md:text-5xl font-black mb-2 transition-colors duration-500 group-hover:text-[#8B31FF] ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                {stat.value}
              </div>
              <div className="text-xs font-semibold mt-5 uppercase tracking-[0.2em] text-[#8B31FF]">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* ═══ Review Submit Modal ═══ */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            onClick={resetAndClose}
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

            {/* Modal Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 30 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              onClick={(e) => e.stopPropagation()}
              className={`relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-lg sm:rounded-3xl border shadow-2xl overflow-x-hidden ${
                isDark
                  ? "bg-[#0B0F19] border-gray-800"
                  : "bg-white border-gray-200"
              }`}
            >
              {/* Modal Header */}
              <div className="relative px-5 sm:px-8 pt-6 sm:pt-8 pb-3 sm:pb-4">
                {/* Close button */}
                <button
                  onClick={resetAndClose}
                  className={`absolute top-3 right-3 sm:top-5 sm:right-5 p-2 rounded-lg transition-colors cursor-pointer ${
                    isDark
                      ? "text-gray-400 hover:text-white hover:bg-gray-800"
                      : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <X size={20} />
                </button>

                <div
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-3 border ${
                    isDark
                      ? "bg-[#8B31FF]/10 text-[#8B31FF] border-[#8B31FF]/30"
                      : "bg-purple-50 text-[#8B31FF] border-purple-200"
                  }`}
                >
                  <Sparkles className="w-3 h-3" />
                  Share Your Experience
                </div>
                <h3
                  className={`text-xl sm:text-2xl font-extrabold pr-8 ${
                    isDark ? "text-white" : "text-gray-900"
                  }`}
                >
                  Write a Review
                </h3>
                <p
                  className={`text-sm mt-1 ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Your feedback helps us grow and inspires future partners.
                </p>
              </div>

              {/* Success State */}
              {submitSuccess ? (
                <div className="px-5 sm:px-8 pb-10 pt-6 text-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 200 }}
                    className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/10 mb-5"
                  >
                    <CheckCircle2 className="w-10 h-10 text-green-500" />
                  </motion.div>
                  <h4
                    className={`text-xl font-bold mb-2 ${
                      isDark ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Thank You!
                  </h4>
                  <p className={isDark ? "text-gray-400" : "text-gray-500"}>
                    Your review has been submitted successfully and is pending
                    approval.
                  </p>
                </div>
              ) : (
                /* Form */
                <form
                  onSubmit={handleSubmit}
                  className="px-5 sm:px-8 pb-6 sm:pb-8 pt-2"
                >
                  {/* Star Rating */}
                  <div className="mb-6">
                    <label
                      className={`text-sm font-bold mb-2 block ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Your Rating
                    </label>
                    <div className="flex items-center gap-1.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onMouseEnter={() => setHoverRating(star)}
                          onMouseLeave={() => setHoverRating(0)}
                          onClick={() =>
                            setFormData({ ...formData, rating: star })
                          }
                          className="cursor-pointer transition-transform hover:scale-110"
                        >
                          <Star
                            size={28}
                            fill={
                              star <= (hoverRating || formData.rating)
                                ? "#FBBF24"
                                : "none"
                            }
                            strokeWidth={
                              star <= (hoverRating || formData.rating) ? 0 : 1.5
                            }
                            className={
                              star <= (hoverRating || formData.rating)
                                ? "text-amber-400"
                                : isDark
                                  ? "text-gray-600"
                                  : "text-gray-300"
                            }
                          />
                        </button>
                      ))}
                      <span
                        className={`ml-2 text-sm font-bold ${
                          isDark ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        {formData.rating}/5
                      </span>
                    </div>
                  </div>

                  {/* Name + Role Row */}
                  <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label
                        className={`text-sm font-bold mb-1.5 block ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Full Name <span className="text-red-400">*</span>
                      </label>
                      <div className="relative">
                        <User
                          size={16}
                          className={`absolute left-3 top-1/2 -translate-y-1/2 ${
                            isDark ? "text-gray-500" : "text-gray-400"
                          }`}
                        />
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) =>
                            setFormData({ ...formData, name: e.target.value })
                          }
                          placeholder="John Doe"
                          className={`w-full pl-9 pr-4 py-2.5 rounded-lg border text-sm font-medium transition-all outline-none focus:ring-2 focus:ring-[#8B31FF]/40 ${
                            isDark
                              ? "bg-gray-900 border-gray-700 text-white placeholder:text-gray-600"
                              : "bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400"
                          }`}
                        />
                      </div>
                    </div>
                    <div>
                      <label
                        className={`text-sm font-bold mb-1.5 block ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Role / Title
                      </label>
                      <div className="relative">
                        <Briefcase
                          size={16}
                          className={`absolute left-3 top-1/2 -translate-y-1/2 ${
                            isDark ? "text-gray-500" : "text-gray-400"
                          }`}
                        />
                        <input
                          type="text"
                          value={formData.role}
                          onChange={(e) =>
                            setFormData({ ...formData, role: e.target.value })
                          }
                          placeholder="CEO at Company"
                          className={`w-full pl-9 pr-4 py-2.5 rounded-lg border text-sm font-medium transition-all outline-none focus:ring-2 focus:ring-[#8B31FF]/40 ${
                            isDark
                              ? "bg-gray-900 border-gray-700 text-white placeholder:text-gray-600"
                              : "bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400"
                          }`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Review Text */}
                  <div className="mb-4">
                    <label
                      className={`text-sm font-bold mb-1.5 block ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Your Review <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      required
                      rows={4}
                      value={formData.text}
                      onChange={(e) =>
                        setFormData({ ...formData, text: e.target.value })
                      }
                      placeholder="Share your experience working with us..."
                      className={`w-full px-4 py-3 rounded-lg border text-sm font-medium transition-all outline-none resize-none focus:ring-2 focus:ring-[#8B31FF]/40 ${
                        isDark
                          ? "bg-gray-900 border-gray-700 text-white placeholder:text-gray-600"
                          : "bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400"
                      }`}
                    />
                  </div>

                  {/* Image Upload */}
                  <div className="mb-6">
                    <label
                      className={`text-sm font-bold mb-1.5 block ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Profile Photo{" "}
                      <span
                        className={`font-normal ${
                          isDark ? "text-gray-500" : "text-gray-400"
                        }`}
                      >
                        (optional, max 200KB)
                      </span>
                    </label>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageChange}
                      accept="image/*"
                      className="hidden"
                    />
                    <div className="flex flex-wrap items-center gap-3">
                      {imagePreview ? (
                        <div className="relative w-14 h-14 rounded-full overflow-hidden border-2 border-[#8B31FF]/40 flex-shrink-0">
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="w-full h-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedImage(null);
                              setImagePreview(null);
                            }}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white cursor-pointer"
                          >
                            <X size={10} />
                          </button>
                        </div>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-all cursor-pointer ${
                          isDark
                            ? "bg-gray-900 border-gray-700 text-gray-300 hover:border-[#8B31FF]/60"
                            : "bg-gray-50 border-gray-200 text-gray-600 hover:border-[#8B31FF]/40"
                        }`}
                      >
                        <Upload size={14} />
                        {imagePreview ? "Change Photo" : "Upload Photo"}
                      </button>
                    </div>
                  </div>

                  {/* Error Message */}
                  {submitError && (
                    <div className="mb-4 px-4 py-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm font-medium">
                      {submitError}
                    </div>
                  )}

                  {/* Submit Button */}
                  <motion.button
                    type="submit"
                    disabled={
                      isSubmitting ||
                      !formData.name.trim() ||
                      !formData.text.trim()
                    }
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full py-3.5 rounded-lg font-bold text-sm shadow-xl transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
                      isDark
                        ? "bg-white text-[#8B31FF] hover:bg-gray-100"
                        : "bg-gradient-to-r from-[#8B31FF] to-[#6d1ec9] text-white hover:shadow-purple-500/30"
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{
                            duration: 1,
                            repeat: Infinity,
                            ease: "linear",
                          }}
                          className="w-5 h-5 border-2 border-current border-t-transparent rounded-full"
                        />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send size={16} />
                        Submit Review
                      </>
                    )}
                  </motion.button>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default TestimonialsCTASection;
